import { api } from "@/lib/api";
import type { HubAiProposedSend, HubAiToolCall } from "@/lib/hubAi";
import type { HubTask, HubTaskInput, HubTaskPriority, HubTaskSource } from "@/lib/tasks";

/** App-wide Hub AI (Tip 179, `/api/ai/assist`). The Slack-only `/api/ai/chat` stays in lib/hubAi. */

export type HubAssistStatus = {
  llm: { configured: boolean; provider?: string; model?: string };
  slack: { connected: boolean; teamName?: string };
};

/** What the user is looking at when they ask, so Hub AI favors that module. */
export type HubAssistContext = {
  module: string;
  label: string;
  path: string;
  /** The item Hub AI was opened from, e.g. one meeting. */
  focus?: Record<string, unknown>;
};

export type HubAssistProposedTask = {
  title: string;
  notes?: string;
  due?: string | null;
  priority?: HubTaskPriority;
};

/** A change Hub AI suggests. Nothing is written until the user confirms its card. */
export type HubAssistProposal =
  | { id: string; kind: "create_tasks"; preview: string; tasks: HubAssistProposedTask[]; source: HubTaskSource }
  | { id: string; kind: "update_task"; preview: string; taskId: string; task: HubTask; changes: HubTaskInput }
  | { id: string; kind: "delete_task"; preview: string; taskId: string; task: HubTask };

export type HubAssistReply = {
  reply: string;
  toolCalls: HubAiToolCall[];
  proposals: HubAssistProposal[];
  proposedSends: HubAiProposedSend[];
  provider?: string;
  model?: string;
  elapsedMs?: number;
};

export type HubAssistMessage = { role: "user" | "assistant"; content: string };

export type HubAssistConfirmResult = { ok: boolean; message: string; tasks: HubTask[] };

export type HubAssistConversationSummary = {
  id: string;
  title: string;
  module: string;
  createdAt: string;
  updatedAt: string;
  count: number;
  preview: string;
};

export type HubAssistStoredMessage = {
  role: "user" | "assistant";
  content: string;
  at: string;
  meta?: Record<string, unknown>;
};

export type HubAssistConversation = {
  id: string;
  title: string;
  module: string;
  createdAt: string;
  updatedAt: string;
  messages: HubAssistStoredMessage[];
};

function localTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function fetchHubAssistStatus(): Promise<HubAssistStatus> {
  return api<HubAssistStatus>("/api/ai/assist/status", { timeoutMs: 15000 });
}

export function askHubAssist(messages: HubAssistMessage[], context: HubAssistContext): Promise<HubAssistReply> {
  return api<HubAssistReply>("/api/ai/assist", {
    method: "POST",
    body: JSON.stringify({ messages, context, timeZone: localTimeZone() }),
    // The API answers within ~50s (nginx gives up at 60s).
    timeoutMs: 65000,
  });
}

/** `include` picks which of a create_tasks proposal's tasks to add (indexes). */
export function confirmHubAssist(proposalId: string, include?: number[]): Promise<HubAssistConfirmResult> {
  return api<HubAssistConfirmResult>("/api/ai/assist/confirm", {
    method: "POST",
    body: JSON.stringify({ proposalId, ...(include ? { include } : {}) }),
    timeoutMs: 20000,
  });
}

export async function listHubAssistConversations(): Promise<HubAssistConversationSummary[]> {
  const { conversations } = await api<{ conversations: HubAssistConversationSummary[] }>("/api/ai/conversations", {
    timeoutMs: 15000,
  });
  return Array.isArray(conversations) ? conversations : [];
}

export async function loadHubAssistConversation(id: string): Promise<HubAssistConversation> {
  const { conversation } = await api<{ conversation: HubAssistConversation }>(
    `/api/ai/conversations/${encodeURIComponent(id)}`,
    { timeoutMs: 15000 },
  );
  return conversation;
}

export function saveHubAssistConversation(
  id: string,
  body: { module: string; messages: HubAssistStoredMessage[] },
): Promise<{ ok: boolean; id: string; title: string; updatedAt: string }> {
  return api(`/api/ai/conversations/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(body),
    timeoutMs: 15000,
  });
}

export function deleteHubAssistConversation(id: string): Promise<{ ok: boolean }> {
  return api(`/api/ai/conversations/${encodeURIComponent(id)}`, { method: "DELETE", timeoutMs: 15000 });
}

export function newConversationId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `c_${Array.from(bytes, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 20)}`;
}

// ---- Opening the panel from anywhere ---------------------------------------------------------

export const HUB_AI_OPEN_EVENT = "atw-hub-ai-open";

export type HubAiOpenDetail = {
  /** Text to put in the composer (or send right away with `send`). */
  prompt?: string;
  send?: boolean;
  /** The item this question is about; sent to Hub AI as context. */
  focus?: Record<string, unknown>;
  /** Start a new conversation first. */
  fresh?: boolean;
};

export function openHubAi(detail: HubAiOpenDetail = {}): void {
  window.dispatchEvent(new CustomEvent<HubAiOpenDetail>(HUB_AI_OPEN_EVENT, { detail }));
}
